import { supabase } from "./supabase";

function toCampaignRow(campaign, userId) {
  return {
    user_id: userId || null,
    name: `${campaign.segment || "Outreach"} - ${campaign.location || "Market"}`,
    segment: campaign.segment || "Property Managers",
    location: campaign.location || "Nairobi",
    offer: campaign.offer || "",
    sender_name: campaign.senderName || "SpaceM8 Team",
    sender_email: campaign.senderEmail || "",
    scheduling_link: campaign.schedulingLink || "",
    status: "Active",
    updated_at: new Date().toISOString(),
  };
}

function fromCampaignRow(row) {
  if (!row) return null;
  return {
    segment: row.segment,
    location: row.location,
    offer: row.offer,
    senderName: row.sender_name,
    senderEmail: row.sender_email,
    schedulingLink: row.scheduling_link,
  };
}

function toLeadRow(lead, campaignId, userId) {
  return {
    id: lead.id,
    campaign_id: campaignId,
    user_id: userId || null,
    company: lead.company,
    contact_name: lead.contactName,
    role: lead.role,
    email: lead.email,
    website: lead.website,
    segment: lead.segment,
    location: lead.location,
    score: lead.score,
    score_tier: lead.scoreTier,
    status: lead.status,
    source: lead.source,
    fit_reason: lead.fitReason,
    signals: lead.signals || [],
    recommended_offer: lead.recommendedOffer,
    draft: lead.draft || "",
    reply_text: lead.replyText || "",
    reply_analysis: lead.replyAnalysis || null,
    reply_draft: lead.replyDraft || "",
    last_updated: lead.lastUpdated || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

function fromLeadRow(row) {
  return {
    id: row.id,
    company: row.company,
    contactName: row.contact_name,
    role: row.role,
    email: row.email,
    website: row.website,
    segment: row.segment,
    location: row.location,
    score: row.score,
    scoreTier: row.score_tier,
    status: row.status,
    source: row.source,
    fitReason: row.fit_reason,
    signals: row.signals || [],
    recommendedOffer: row.recommended_offer,
    lastUpdated: row.last_updated,
    draft: row.draft || "",
    replyText: row.reply_text || "",
    replyAnalysis: row.reply_analysis || null,
    replyDraft: row.reply_draft || "",
  };
}

export async function loadLatestOutreachWorkspace() {
  const { data: campaign, error: campaignError } = await supabase
    .from("outreach_campaigns")
    .select("*")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (campaignError) throw campaignError;
  if (!campaign) return null;

  const { data: leads, error: leadsError } = await supabase
    .from("outreach_leads")
    .select("*")
    .eq("campaign_id", campaign.id)
    .order("score", { ascending: false });

  if (leadsError) throw leadsError;

  return {
    campaignId: campaign.id,
    campaign: fromCampaignRow(campaign),
    leads: (leads || []).map(fromLeadRow),
  };
}

export async function saveOutreachCampaign(campaignId, campaign, userId) {
  const row = toCampaignRow(campaign, userId);

  if (campaignId) {
    const { data, error } = await supabase
      .from("outreach_campaigns")
      .upsert({ id: campaignId, ...row })
      .select()
      .maybeSingle();

    if (error) throw error;
    if (data?.id) return data.id;
  }

  const { data, error } = await supabase
    .from("outreach_campaigns")
    .insert([row])
    .select()
    .single();

  if (error) throw error;
  return data.id;
}

export async function replaceOutreachLeads(campaignId, leads, userId) {
  const { error: deleteError } = await supabase
    .from("outreach_leads")
    .delete()
    .eq("campaign_id", campaignId);

  if (deleteError) throw deleteError;
  if (!leads.length) return [];

  const { data, error } = await supabase
    .from("outreach_leads")
    .insert(leads.map((lead) => toLeadRow(lead, campaignId, userId)))
    .select();

  if (error) throw error;
  return (data || []).map(fromLeadRow);
}

export async function saveOutreachLead(campaignId, lead, userId) {
  const { data, error } = await supabase
    .from("outreach_leads")
    .upsert(toLeadRow(lead, campaignId, userId))
    .select()
    .single();

  if (error) throw error;
  return fromLeadRow(data);
}

export async function saveOutreachLeads(campaignId, leads, userId) {
  if (!leads.length) return [];

  const { data, error } = await supabase
    .from("outreach_leads")
    .upsert(leads.map((lead) => toLeadRow(lead, campaignId, userId)))
    .select();

  if (error) throw error;
  return (data || []).map(fromLeadRow);
}

export async function createOutreachMessage({ campaignId, lead, userId, direction, status, body }) {
  const firstLine = String(body || "").split("\n")[0] || "";
  const subject = firstLine.replace(/^subject:\s*/i, "").trim();

  const { error } = await supabase.from("outreach_messages").insert([
    {
      campaign_id: campaignId,
      lead_id: lead.id,
      user_id: userId || null,
      direction,
      subject,
      body,
      status,
      approved_at: status === "Approved" ? new Date().toISOString() : null,
    },
  ]);

  if (error) throw error;
}

export async function createOutreachReplyEvent({ campaignId, lead, userId, replyText, analysis }) {
  const { error } = await supabase.from("outreach_reply_events").insert([
    {
      campaign_id: campaignId,
      lead_id: lead.id,
      user_id: userId || null,
      sender_email: lead.email,
      body: replyText,
      intent: analysis.intent,
      confidence: analysis.confidence,
      next_step: analysis.nextStep,
      raw_payload: {
        status: analysis.status,
      },
    },
  ]);

  if (error) throw error;
}

export async function sendApprovedOutreachEmail({ campaignId, leadId, draft }) {
  const { data, error } = await supabase.functions.invoke("outreach-send-email", {
    body: {
      campaignId,
      leadId,
      draft,
    },
  });

  if (error) throw error;
  return data;
}
